/**
 * Customers Tab Component
 *
 * Displays full customer list on load, with a single search box that matches an
 * order number, phone, email or name.
 * Shows customer details, including their Dee Rewards standing.
 *
 * Part of Phase 5: User Management Admin Page
 */

import { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Receipt as OrderIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import authInstance from '../../services/httpClient';
import { queryKeys } from '../../hooks/queries';
import { CustomerDetailsModal } from './CustomerDetailsModal';
import { useAdminTheme } from '../../admin-theme';

interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  totalOrders: number;
  totalOrderValue: number;
  lastOrderAt: string | null;
  createdAt: string;
}

type SortField = 'name' | 'createdAt' | 'totalOrders' | 'totalOrderValue' | 'lastOrderAt';
type SortDirection = 'asc' | 'desc';

// Numeric and date columns are most useful biggest/most-recent first, so that's
// what the first click gives you. Name starts A–Z.
const FIRST_CLICK_DIRECTION: Record<SortField, SortDirection> = {
  name: 'asc',
  createdAt: 'desc',
  totalOrders: 'desc',
  totalOrderValue: 'desc',
  lastOrderAt: 'desc',
};

// Email and phone have no sortField — they stay plain headings.
const COLUMNS: Array<{
  label: string;
  align?: 'left' | 'right';
  sortField?: SortField;
}> = [
  { label: 'Name', sortField: 'name' },
  { label: 'Email' },
  { label: 'Phone' },
  // Signup date. First click gives newest-first; click again for the oldest.
  { label: 'Joined', sortField: 'createdAt' },
  { label: 'Total Orders', align: 'right', sortField: 'totalOrders' },
  { label: 'Lifetime Value', align: 'right', sortField: 'totalOrderValue' },
  { label: 'Last Order', sortField: 'lastOrderAt' },
];

export const CustomersTab = () => {
  const { colors, elevatedInputStyle } = useAdminTheme();

  const [searchValue, setSearchValue] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [activeSearchParams, setActiveSearchParams] = useState<Record<string, string>>({});
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const notify = useNotify();

  const { data: customers = [], isLoading: loading, error: queryError, refetch } = useQuery({
    queryKey: queryKeys.customers.list(activeSearchParams),
    queryFn: async () => {
      const response = await authInstance.get('admin/users/customers/search', {
        params: Object.keys(activeSearchParams).length > 0 ? activeSearchParams : undefined,
      });
      if (!response.data || typeof response.data !== 'object') {
        console.error('[CustomersTab] Invalid response:', {
          status: response.status,
          dataType: typeof response.data,
          dataPreview: typeof response.data === 'string' ? response.data.substring(0, 200) : response.data,
          contentType: response.headers?.['content-type'],
          contentEncoding: response.headers?.['content-encoding'],
        });
        throw new Error(`Empty response from server (got ${typeof response.data}: ${JSON.stringify(response.data)?.substring(0, 100)})`);
      }
      return (response.data.customers || []) as Customer[];
    },
    staleTime: 0,
    gcTime: 2 * 60_000,
  });

  const error = queryError
    ? ((queryError as any)?.response?.data?.message || (queryError as Error).message || 'Search failed')
    : null;

  // Click a heading once to sort by it, again to flip the direction.
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection(FIRST_CLICK_DIRECTION[field]);
  };

  const sortedCustomers = useMemo(() => {
    const factor = sortDirection === 'asc' ? 1 : -1;

    return [...customers].sort((a, b) => {
      switch (sortField) {
        case 'totalOrders':
          return (a.totalOrders - b.totalOrders) * factor;

        case 'totalOrderValue':
          return (a.totalOrderValue - b.totalOrderValue) * factor;

        case 'createdAt':
          // Every customer has a signup date, so no null handling needed here
          // (unlike lastOrderAt below).
          return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * factor;

        case 'lastOrderAt': {
          // Customers who have never ordered sit at the bottom either way —
          // "Never" isn't a date and shouldn't win "most recent".
          if (!a.lastOrderAt && !b.lastOrderAt) return 0;
          if (!a.lastOrderAt) return 1;
          if (!b.lastOrderAt) return -1;
          return (new Date(a.lastOrderAt).getTime() - new Date(b.lastOrderAt).getTime()) * factor;
        }

        case 'name':
        default:
          return (
            `${a.firstName} ${a.lastName}`.trim().localeCompare(
              `${b.firstName} ${b.lastName}`.trim(),
              'en-ZA',
              { sensitivity: 'base' },
            ) * factor
          );
      }
    });
  }, [customers, sortField, sortDirection]);

  const handleSearch = () => {
    if (!searchValue.trim()) {
      notify('Please enter a search term', { type: 'warning' });
      return;
    }

    // One unified param — the backend works out whether it's an order number,
    // phone, email or name.
    const params: Record<string, string> = { search: searchValue.trim() };

    setActiveSearchParams(params);
  };

  const handleViewDetails = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCustomerId(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      <Box px={3} mb={3}>
        <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 0.5 }}>
          Customer Management
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          View all customers, or search by order number, phone, email or name. POPI Compliant - Limited data exposure.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* One box, four things — order number, phone, email or name. Making
              the user pick the kind of thing they just typed is busywork. */}
          {/* Was md:7 — took back the 1.5 the Map button used to occupy before
              the map moved to its own page under Admin Tools. */}
          <Grid size={{ xs: 12, md: 8.5 }}>
            <TextField
              fullWidth
              label="Search by order number, phone, email or name"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={elevatedInputStyle}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} sx={{ color: colors.info }} /> : <SearchIcon />}
              sx={{
                height: '56px',
                color: colors.info,
                borderColor: `${colors.info}60`,
                bgcolor: `${colors.info}10`,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: `${colors.info}20`,
                  borderColor: colors.info,
                  boxShadow: `0 0 15px ${colors.info}30`,
                },
              }}
            >
              Search
            </Button>
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { setActiveSearchParams({}); }}
              disabled={loading}
              startIcon={<RefreshIcon />}
              sx={{
                height: '56px',
                color: colors.textSecondary,
                borderColor: colors.border,
                bgcolor: colors.bgCard,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: colors.bgElevated,
                  borderColor: colors.textSecondary,
                  color: colors.textPrimary,
                },
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Box px={3} mb={2}>
          <Alert
            severity="error"
            sx={{
              bgcolor: 'rgba(255, 0, 60, 0.1)',
              border: `1px solid ${colors.errorGlow}40`,
              borderRadius: 2,
              color: colors.textPrimary,
              '& .MuiAlert-icon': { color: colors.errorGlow },
            }}
          >
            {error}
          </Alert>
        </Box>
      )}

      {customers.length > 0 && (
        <TableContainer
          sx={{
            bgcolor: colors.bgCard,
            borderRadius: 2,
            border: `1px solid ${colors.border}`,
            mx: 3,
            // maxHeight makes the container the scroller, which is what `stickyHeader`
            // pins the header row to — without it the header scrolls off with the page.
            maxHeight: 'calc(100vh - 300px)',
            minHeight: 320,
            // Sticky cells are transparent by default and would show rows through.
            '& .MuiTableHead-root .MuiTableCell-root': { bgcolor: colors.bgCard },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map(({ label, align, sortField: field }) => (
                  <TableCell
                    key={label}
                    align={align}
                    sortDirection={field && sortField === field ? sortDirection : false}
                    sx={{
                      color: colors.textMuted,
                      borderBottom: `1px solid ${colors.border}`,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {field ? (
                      <TableSortLabel
                        active={sortField === field}
                        direction={sortField === field ? sortDirection : FIRST_CLICK_DIRECTION[field]}
                        onClick={() => handleSort(field)}
                        sx={{
                          color: 'inherit',
                          '&:hover': { color: colors.textPrimary },
                          '&.Mui-active': {
                            color: colors.textPrimary,
                            '& .MuiTableSortLabel-icon': { color: `${colors.info} !important` },
                          },
                        }}
                      >
                        {label}
                      </TableSortLabel>
                    ) : (
                      label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCustomers.map((customer) => (
                <TableRow
                  key={customer.id}
                  onClick={() => handleViewDetails(customer.id)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: colors.bgElevated },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
                    {customer.firstName} {customer.lastName}
                  </TableCell>
                  <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                    {customer.email}
                  </TableCell>
                  <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                    {customer.phoneNumber}
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, borderBottom: `1px solid ${colors.border}`, whiteSpace: 'nowrap' }}>
                    {formatDate(customer.createdAt)}
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Chip
                      label={customer.totalOrders}
                      size="small"
                      sx={{
                        bgcolor: `${colors.info}15`,
                        color: colors.info,
                        border: `1px solid ${colors.info}40`,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: colors.successGlow }}>
                      {formatCurrency(customer.totalOrderValue)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
                    {formatDate(customer.lastOrderAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {customers.length === 0 && !loading && !error && (
        <Box p={4} textAlign="center">
          <Typography sx={{ color: colors.textMuted }}>
            No customers found. Try adjusting your search criteria.
          </Typography>
        </Box>
      )}

      {loading && customers.length === 0 && (
        <Box p={4} textAlign="center">
          <CircularProgress sx={{ color: colors.info }} />
          <Typography sx={{ color: colors.textSecondary, mt: 2 }}>
            Loading customers...
          </Typography>
        </Box>
      )}

      {selectedCustomerId && (
        <CustomerDetailsModal
          open={modalOpen}
          customerId={selectedCustomerId}
          onClose={handleModalClose}
        />
      )}
    </Box>
  );
};
